import { createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "aws-amplify";

//sentry error tracking
import * as Sentry from "@sentry/nextjs";
import { pyFetch } from "src/utils/pyApi";


export const authorizeWithGoogle = createAsyncThunk(
  "auth/authorizeWithGoogle",
  async () => {
    return await API.get("auth", `/auth/google`, {});
  }
);

export const getGoogleToken = createAsyncThunk(
  "auth/getGoogleToken",
  async (code: string) => {
    // Multiple detection methods for OAuth callback window (popup OR new tab)
    const isOAuthCallback = window.location.pathname.includes('/authentication/oAuth2/authorization');
    const hasOpener = window.opener !== null;
    const isNamedWindow = window.name === 'newWindow';
    const currentUrl = window.location.href;
    const hasAuthCode = currentUrl.includes('code=') && currentUrl.includes('scope=');

    // If ANY of these are true, we're likely in the OAuth callback window
    const shouldStoreToLocalStorage = isOAuthCallback || hasOpener || isNamedWindow || hasAuthCode;

    console.log('🔵 getGoogleToken called - Detection results:', {
      isOAuthCallback,
      hasOpener,
      isNamedWindow,
      hasAuthCode,
      shouldStoreToLocalStorage,
      pathname: window.location.pathname,
      windowName: window.name,
      codePreview: code.substring(0, 20) + '...'
    });

    // If in OAuth callback window (popup OR tab), store directly to localStorage
    if (shouldStoreToLocalStorage) {
      console.log('📦 OAuth callback window detected - storing directly to localStorage without API call');

      const authData = {
        code,
        scope: 'https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/documents',
        timestamp: Date.now()
      };

      localStorage.setItem('google_auth_pending', JSON.stringify(authData));
      console.log('✅ Stored auth data in localStorage:', authData);

      // Verify it was stored
      const verify = localStorage.getItem('google_auth_pending');
      console.log('🔍 Verification - localStorage contains:', verify ? 'Data stored successfully' : 'ERROR - Not stored!');

      // Try to close popup after a short delay
      setTimeout(() => {
        try {
          window.close();
          console.log('🚪 Popup close attempted');
        } catch (e) {
          console.log('⚠️ Could not close popup:', e);
        }
      }, 1000);

      // Return success to prevent error propagation
      return { success: true, message: 'Auth code stored for main window processing' };
    }

    // We're in the main window - proceed with API call
    console.log('🏠 Main window detected - making API call');

    try {
      const result = await API.get("auth", `/auth/google/callback?code=${code}`, {});
      console.log('✅ API call successful:', result);
      return result;
    } catch (err: any) {
      console.error('❌ API call failed:', err);

      // Log full error structure for debugging
      console.log('📋 Error details:', {
        status: err?.response?.status,
        statusCode: err?.statusCode,
        code: err?.code,
        message: err?.message,
        name: err?.name,
        fullError: err
      });

      Sentry.captureException(err);
      throw err;
    }
  }
);
//seaprch console auth
export const authorizeWithSearchConsole = createAsyncThunk(
  "auth/authorizeWithSearchConsole",
  async () => {
    return await API.get("auth", `/auth/google/searchConsole`, {});
  }
);

export const getSearchConsoleToken = createAsyncThunk(
  "auth/getSearchConsoleToken",
  async (code: string) => {
    try {
      return await API.get(
        "auth",
        `/auth/google/searchConsole/callback?code=${code}`,
        {}
      );
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  }
);

export const getActivePlan = createAsyncThunk(
  "auth/getActivePlan",
  async () => {
    try {
      return await API.get("auth", `/auth/user/activePlan`, {});
    }
    catch (err) {
      Sentry.captureException(err);
      throw err;
    }

  }
);
export const getInvoices = createAsyncThunk("auth/getInvoices", async () => {
  try {
    return await API.get("auth", `/auth/user/invoices`, {});
  }
  catch (err) {
    Sentry.captureException(err);
    throw err;
  }

});

export const getBriefsStatistics = createAsyncThunk(
  "auth/getBriefsStatistics",
  async ({ startDate, endDate }: { startDate: number; endDate: number }) => {
    try {
      return await API.get(
        "auth",
        `/auth/statistics/briefs?startDate=${startDate}&endDate=${endDate}`,
        {}
      );
    }
    catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  }
);

export const getUsers = createAsyncThunk(
  "auth/getUsers",
  async ({
    limit,
    paginationToken,
    email,
  }: {
    limit: number;
    paginationToken?: string;
    email?: string;
  }) => {
    try {
      return await API.get(
        "auth",
        `/auth/users?limit=${limit}${paginationToken
          ? `&paginationToken=${encodeURIComponent(paginationToken)}`
          : ""
        }${email ? `&email=${email}` : ""}`,
        {}
      );
    }
    catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  }
);

export const getUser = createAsyncThunk("auth/getUser", async (id: string) => {
  return await API.get("auth", `/auth/users/${id}`, {});
});

export const getUserBriefs = createAsyncThunk(
  "auth/getUserBriefs",
  async ({
    id,
    limit,
    paginationToken,
  }: {
    id: string;
    limit: number;
    paginationToken?: { id: string; createdAt: string };
  }) => {
    try {
      return await API.get(
        "brief",
        `/briefs/getByUser/${id}?limit=${limit}${paginationToken
          ? `&paginationTokenId=${encodeURIComponent(
            paginationToken.id
          )}&paginationTokenCreatedAt=${encodeURIComponent(
            paginationToken.createdAt
          )}`
          : ""
        }`,
        {}
      );
    }
    catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  }
);

//get user content Updates
export const getUserContentUpdates = createAsyncThunk(
  "auth/getUserContentUpdates",
  async ({
    userId,
    limit,
    page,
    keyword,
    project
  }: {
    userId: string;
    limit: number;
    page: number;
    keyword?: string;
    project?: string;
  }) => {


    try {
      const data = await pyFetch(`/optimize/get-articles`, {
        method: "POST",
        body: JSON.stringify({ userId, page, limit, keyword, project }),
      });
      return data.json();
    } catch (err) {
      console.log(err);
    }
  }


);